import { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { serviceEndpoints } from '../../services/api/endpoints/service.endpoint';
import { CreateServiceDTO, UpdateServiceDTO } from '../../types/service';
import { logger } from '../../utils/logger';

export const useServiceActions = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedOrganizationId = useSelector(
    (state: RootState) => state.organizationContext.selectedOrganizationId
  );

  const createService = async (data: CreateServiceDTO) => {
    if (!selectedOrganizationId) {
      throw new Error('No organization selected');
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await serviceEndpoints.createService(selectedOrganizationId, data);
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create service';
      setError(message);
      logger.error('Error creating service:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const updateService = async (serviceId: number, data: UpdateServiceDTO) => {
    if (!selectedOrganizationId) {
      throw new Error('No organization selected');
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await serviceEndpoints.updateService(selectedOrganizationId, serviceId, data);
      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update service';
      setError(message);
      logger.error('Error updating service:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteService = async (serviceId: number) => {
    if (!selectedOrganizationId) {
      throw new Error('No organization selected');
    }

    setIsLoading(true);
    setError(null);

    try {
      await serviceEndpoints.deleteService(selectedOrganizationId, serviceId);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete service';
      setError(message);
      logger.error('Error deleting service:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    createService,
    updateService,
    deleteService,
    isLoading,
    error,
  };
};
